import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Plus, Minus, Trash2, ShoppingBag, Sparkles, MapPin, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { ScrollArea } from '../ui/scroll-area';
import { useCart } from '../../context/CartContext';
import { menuItems, locations } from '../../data/menuData';
import { motion, AnimatePresence } from 'framer-motion';

// Upsell suggestions based on cart contents
function getUpsellSuggestions(cartItems) {
  const hasMain = cartItems.some(item => ['mains', 'bowls', 'popular'].includes(item.category));
  const hasDrink = cartItems.some(item => item.category === 'drinks');
  const hasDessert = cartItems.some(item => item.category === 'desserts');
  
  const suggestions = [];
  
  if (hasMain && !hasDrink) {
    const drinks = menuItems.filter(item => item.category === 'drinks').slice(0, 2);
    suggestions.push(...drinks);
  }
  
  if (hasMain && !hasDessert) {
    const desserts = menuItems.filter(item => item.category === 'desserts').slice(0, 1);
    suggestions.push(...desserts);
  }
  
  return suggestions.slice(0, 2);
}

function CartItem({ item, onUpdateQuantity, onRemove, onUpdateNote }) {
  const [showNote, setShowNote] = useState(!!item.note);
  
  let itemPrice = item.price;
  if (item.selectedVariants) {
    Object.values(item.selectedVariants).forEach(v => { itemPrice += v.price || 0; });
  }
  if (item.selectedExtras) {
    item.selectedExtras.forEach(e => { itemPrice += e.price || 0; });
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -100 }}
      className="py-4 border-b border-border/50 last:border-0"
    >
      <div className="flex gap-3">
        <img 
          src={item.image} 
          alt={item.name}
          className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h4 className="font-medium text-sm leading-tight">{item.name}</h4>
              {/* Show selected variants */}
              {item.selectedVariants && Object.entries(item.selectedVariants).length > 0 && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  {Object.values(item.selectedVariants).map(v => v.name).join(', ')}
                </p>
              )}
              {/* Show selected extras */}
              {item.selectedExtras && item.selectedExtras.length > 0 && (
                <p className="text-xs text-accent mt-0.5">
                  + {item.selectedExtras.map(e => e.name).join(', ')}
                </p>
              )}
            </div>
            <button 
              onClick={() => onRemove(item.cartId)}
              className="p-1 text-muted-foreground hover:text-destructive transition-colors"
              data-testid={`remove-item-${item.cartId}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-2">
              <button
                onClick={() => onUpdateQuantity(item.cartId, item.quantity - 1)}
                disabled={item.quantity <= 1}
                className="w-7 h-7 rounded-full border border-border flex items-center justify-center hover:bg-secondary transition-colors disabled:opacity-50"
                data-testid={`decrease-qty-${item.cartId}`}
              >
                <Minus className="w-3 h-3" />
              </button>
              <span className="text-sm font-medium w-6 text-center">{item.quantity}</span>
              <button
                onClick={() => onUpdateQuantity(item.cartId, item.quantity + 1)}
                className="w-7 h-7 rounded-full border border-border flex items-center justify-center hover:bg-secondary transition-colors"
                data-testid={`increase-qty-${item.cartId}`}
              >
                <Plus className="w-3 h-3" />
              </button>
            </div>
            <span className="font-medium text-sm">${(itemPrice * item.quantity).toFixed(0)}</span>
          </div>
          
          {/* Note toggle */}
          {!showNote ? (
            <button 
              onClick={() => setShowNote(true)}
              className="text-xs text-muted-foreground hover:text-foreground mt-2 transition-colors"
            >
              + Agregar nota
            </button>
          ) : (
            <Input
              value={item.note || ''}
              onChange={(e) => onUpdateNote(item.cartId, e.target.value)}
              placeholder="Ej: Sin cebolla, extra salsa..."
              className="mt-2 h-8 text-xs bg-secondary/50 border-0"
              data-testid={`item-note-${item.cartId}`}
            />
          )}
        </div>
      </div>
    </motion.div>
  );
}

function UpsellItem({ item, onAdd }) {
  return (
    <motion.button
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      onClick={() => onAdd({
        ...item,
        quantity: 1,
        selectedVariants: {},
        selectedExtras: [],
        note: '',
      })}
      className="flex items-center gap-3 p-2 rounded-lg bg-accent/5 border border-accent/20 hover:bg-accent/10 transition-all w-full text-left"
      data-testid={`upsell-${item.id}`}
    >
      <img src={item.image} alt={item.name} className="w-12 h-12 rounded-lg object-cover" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{item.name}</p>
        <p className="text-xs text-accent">+${item.price}</p>
      </div>
      <Plus className="w-4 h-4 text-accent flex-shrink-0" />
    </motion.button>
  );
}

export function CartDrawer() {
  const {
    isOpen,
    closeCart,
    items,
    updateQuantity,
    removeItem,
    updateNote,
    addItem,
    subtotal,
    deliveryFee,
    total,
    freeDeliveryRemaining,
    selectedLocation,
    setLocation,
    tip,
    setTip,
  } = useCart();

  const upsellSuggestions = getUpsellSuggestions(items);
  const tipOptions = [0, 15, 20, 25];

  return (
    <Sheet open={isOpen} onOpenChange={closeCart}>
      <SheetContent 
        side="right" 
        className="w-full sm:max-w-md p-0 flex flex-col"
        data-testid="cart-drawer"
      >
        <SheetHeader className="px-6 py-4 border-b border-border/50">
          <SheetTitle className="flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" />
            Tu pedido
            {items.length > 0 && (
              <span className="text-sm font-normal text-muted-foreground">
                ({items.length} {items.length === 1 ? 'producto' : 'productos'})
              </span>
            )}
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
            <div className="w-20 h-20 rounded-full bg-secondary flex items-center justify-center mb-4">
              <ShoppingBag className="w-10 h-10 text-muted-foreground" />
            </div>
            <h3 className="font-medium text-lg">Tu carrito está vacío</h3>
            <p className="text-muted-foreground text-center mt-2">
              Explora nuestro menú y agrega tus platillos favoritos
            </p>
            <Link to="/menu" onClick={closeCart}>
              <Button className="mt-6 rounded-full" data-testid="browse-menu-btn">
                Ver menú
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <ScrollArea className="flex-1 px-6">
              {/* Location selector */}
              <div className="py-4 border-b border-border/50">
                <label className="text-xs text-muted-foreground uppercase tracking-wide">
                  Sucursal
                </label>
                <select
                  value={selectedLocation?.id || ''}
                  onChange={(e) => {
                    const loc = locations.find(l => l.id === parseInt(e.target.value));
                    setLocation(loc);
                  }}
                  className="w-full mt-2 h-10 px-3 bg-secondary/50 border-0 rounded-lg text-sm focus:ring-2 focus:ring-accent"
                  data-testid="location-select"
                >
                  <option value="">Selecciona sucursal</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
                {selectedLocation && (
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    Tiempo estimado: {selectedLocation.avgPrepTime} min
                  </p>
                )}
              </div>

              {/* Free delivery progress */}
              {selectedLocation && freeDeliveryRemaining > 0 && (
                <div className="py-3 bg-accent/5 -mx-6 px-6">
                  <p className="text-sm">
                    <span className="text-accent font-medium">¡Faltan ${freeDeliveryRemaining.toFixed(0)}</span>
                    <span className="text-muted-foreground"> para envío gratis!</span>
                  </p>
                  <div className="mt-2 h-1.5 bg-secondary rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-accent transition-all duration-500"
                      style={{ width: `${Math.min(100, (subtotal / selectedLocation.freeDeliveryMin) * 100)}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Cart Items */}
              <AnimatePresence mode="popLayout">
                {items.map(item => (
                  <CartItem 
                    key={item.cartId}
                    item={item}
                    onUpdateQuantity={updateQuantity}
                    onRemove={removeItem}
                    onUpdateNote={updateNote}
                  />
                ))}
              </AnimatePresence>

              {/* Upsell suggestions */}
              {upsellSuggestions.length > 0 && (
                <div className="py-4 border-t border-border/50">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-4 h-4 text-accent" />
                    <span className="text-sm font-medium">¿Te gustaría agregar?</span>
                  </div>
                  <div className="space-y-2">
                    {upsellSuggestions.map(item => (
                      <UpsellItem key={item.id} item={item} onAdd={addItem} />
                    ))}
                  </div>
                </div>
              )}

              {/* Tip selector */}
              <div className="py-4 border-t border-border/50">
                <label className="text-xs text-muted-foreground uppercase tracking-wide">
                  Propina
                </label>
                <div className="flex gap-2 mt-2">
                  {tipOptions.map(pct => (
                    <button
                      key={pct}
                      onClick={() => setTip(pct === 0 ? 0 : Math.round(subtotal * pct / 100))}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                        (pct === 0 && tip === 0) || (pct > 0 && tip === Math.round(subtotal * pct / 100))
                          ? 'bg-accent text-accent-foreground'
                          : 'bg-secondary hover:bg-secondary/80'
                      }`}
                      data-testid={`tip-${pct}`}
                    >
                      {pct === 0 ? 'Sin' : `${pct}%`}
                    </button>
                  ))}
                </div>
              </div>
            </ScrollArea>

            {/* Summary and Checkout */}
            <div className="px-6 py-4 border-t border-border/50 bg-background">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>${subtotal.toFixed(0)}</span>
                </div>
                {selectedLocation && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Envío</span>
                    <span className={deliveryFee === 0 ? 'text-accent' : ''}>
                      {deliveryFee === 0 ? 'Gratis' : `$${deliveryFee}`}
                    </span>
                  </div>
                )}
                {tip > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Propina</span>
                    <span>${tip.toFixed(0)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-border/50 text-base font-medium">
                  <span>Total</span>
                  <span>${total.toFixed(0)}</span>
                </div>
              </div>
              
              <Link to="/checkout" onClick={closeCart}>
                <Button 
                  className="w-full mt-4 h-12 rounded-full font-medium text-base"
                  disabled={!selectedLocation}
                  data-testid="checkout-btn"
                >
                  {selectedLocation ? 'Continuar al checkout' : 'Selecciona una sucursal'}
                  <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
              </Link>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
